// pages/MyMediaPost.js
//
// TikTok-style "my posts" page for Moment (media) posts.
//
//   1. PROFILE GRID  — avatar, @username, totals (posts / likes / views) and a
//      3-column grid of 9:16 thumbnails. Each tile shows the view count
//      bottom-left (like TikTok's play count), a multi-media badge top-right,
//      and a "Posting…" veil while the post is still optimistic.
//   2. FULL-SCREEN VIEWER — tap any tile to open the post full-bleed. Swipe /
//      wheel / arrow keys move through YOUR posts, tap pauses the video, a
//      seekable progress bar sits at the bottom, and the same right-side
//      action rail as Moment.js is shown (like / comment / views / support /
//      share). The comment icon opens <MediaComment /> exactly like Moment.
//
// Works for the logged-in user (myMediaPosts) and for another author
// (viewedMediaPostsUser + viewedAuthorMediaPosts) — same split MyPost.js uses.
//
// MEDIA SHAPE (same as Moment.js):
//   post.media = [{ type, url, thumbnail, duration, width, height, ... }]
//   The FIRST media item is the one displayed. A legacy single `media`
//   object is still supported.
//
// EMBEDDED MODE (embedded={true}) — used inside UserInfoOverlay's "Media" tab.
//   Renders ONLY the thumbnail grid (the overlay already shows the avatar and
//   name), uses the parent's scroll container, and opens the full-screen
//   viewer in a portal on <body> above the overlay.
//
// Usage (see DesktopSocialLayout / MobileAppLayout):
//   <MyMediaPost
//     myChatId={myChatId}
//     users={users}
//     currentUser={currentUser}
//     avatarUrl={avatarUrl}
//     myUserName={myUserName}
//     myDisplayName={myDisplayName}
//     myMediaPosts={viewedMediaPostsUser ? viewedAuthorMediaPosts : myMediaPosts}
//     viewedUser={viewedMediaPostsUser}
//     mediaCommentsByPost={mediaCommentsByPost}
//     onLikeMediaPost={mediaSocket?.mediaLikePost}
//     onAddMediaComment={mediaSocket?.mediaAddComment}
//     onLikeMediaComment={mediaSocket?.mediaLikeComment}
//     onSupportMediaPost={mediaSocket?.mediaSupportPost}
//     onTrackMediaView={mediaSocket?.mediaTrackView}
//     onBack={onExitAuthorMediaPosts}
//   />

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  HeartIcon,
  CommentIcon,
  EyeIcon,
  SupportIcon,
  ShareIcon,
  MusicIcon,
} from "../components/mediaIcon";
import MediaComment from "../cards/mediaComment";

/* =========================================================
   HELPERS
========================================================= */

const formatCount = (value) => {
  const num = Number(value) || 0;
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(num % 1_000 === 0 ? 0 : 1)}K`;
  }
  return `${num}`;
};

const formatTime = (sec = 0) => {
  const t = Math.max(0, Math.floor(sec || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};

const getMediaItems = (post) =>
  Array.isArray(post?.media) ? post.media : post?.media ? [post.media] : [];

const getFirstMedia = (post) => getMediaItems(post)[0] || null;

const getPostType = (post) =>
  getFirstMedia(post)?.type || post?.mediaType || null;

const getPostUrl = (post) =>
  getFirstMedia(post)?.url || post?.coverThumbnail || null;

const getPostThumb = (post) =>
  getFirstMedia(post)?.thumbnail || post?.coverThumbnail || null;

const getPostCaption = (post) => post?.caption || post?.text || "";

/* =========================================================
   ICONS (local — not in mediaIcon.js)
========================================================= */

function BackIcon({ size = 22, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M15 5l-7 7 7 7"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlayBadgeIcon({ size = 12, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <path d="M7 4.5v15l13-7.5z" />
    </svg>
  );
}

function StackIcon({ size = 16, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="8" y="3" width="13" height="13" rx="2.5" fill={color} />
      <path
        d="M5.5 8H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-.5"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* =========================================================
   📸 GRID TILE
========================================================= */

function PostTile({ post, onOpen }) {
  const type = getPostType(post);
  const url = getPostUrl(post);
  const thumb = getPostThumb(post);
  const multi = getMediaItems(post).length > 1;
  const pending = !!post?.isOptimistic;

  return (
    <button
      type="button"
      onClick={() => onOpen(post.postId)}
      style={styles.tile}
      aria-label="Open post"
    >
      {type === "video" ? (
        thumb ? (
          <img src={thumb} alt="" style={styles.tileMedia} loading="lazy" />
        ) : url ? (
          // No stored thumbnail: show the first frame of the video itself.
          <video
            src={`${url}#t=0.1`}
            preload="metadata"
            muted
            playsInline
            style={styles.tileMedia}
          />
        ) : (
          <div style={styles.tileEmpty} />
        )
      ) : url ? (
        <img src={url} alt="" style={styles.tileMedia} loading="lazy" />
      ) : (
        <div style={styles.tileEmpty} />
      )}

      <div style={styles.tileScrim} />

      {multi ? (
        <span style={styles.tileBadge}>
          <StackIcon size={15} />
        </span>
      ) : null}

      <span style={styles.tileViews}>
        {type === "video" ? <PlayBadgeIcon size={11} /> : <EyeIcon size={14} />}
        <span>{formatCount(post?.viewsCount)}</span>
      </span>

      {pending ? <div style={styles.tilePending}>Posting…</div> : null}
    </button>
  );
}

/* =========================================================
   🎬 FULL-SCREEN VIEWER
========================================================= */

function PostViewer({
  embedded = false,
  posts,
  index,
  setIndex,
  onClose,
  author,
  users,
  currentUser,
  avatarUrl,
  myChatId,
  mediaCommentsByPost,
  onLikeMediaPost,
  onAddMediaComment,
  onLikeMediaComment,
  onSupportMediaPost,
  onSupportMediaComment,
  onViewMediaCommentEarnings,
  onTrackMediaCommentView,
  onTrackMediaView,
}) {
  const post = posts[index] || null;

  // A post that uses a library / someone else's sound plays THAT audio and
  // its own video is muted (same rule as Moment.js).
  const postSound = post?.sound || null;
  const hasCustomSound = !!(postSound?.url && !postSound.isOriginal);

  const [showComments, setShowComments] = useState(false);
  const [captionExpanded, setCaptionExpanded] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isScrubbing, setIsScrubbing] = useState(false);
  // true when the browser refused to start the sound track without a gesture
  const [soundBlocked, setSoundBlocked] = useState(false);

  const videoRef = useRef(null);
  const soundAudioRef = useRef(null);
  const lastVideoTimeRef = useRef(0);
  const userPausedRef = useRef(false);
  const rootRef = useRef(null);
  const wheelLockRef = useRef(false);
  const touchStartYRef = useRef(null);

  const type = getPostType(post);
  // Photo post that plays a sound: the <audio> element acts as the "player"
  const isPhotoSound = !!post && type !== "video" && hasCustomSound;
  const url = getPostUrl(post);
  const thumb = getPostThumb(post);
  const caption = getPostCaption(post);
  const captionBody = useMemo(
    () => caption.replace(/#[\w]+/g, "").trim(),
    [caption]
  );
  const hashtags = useMemo(() => {
    if (Array.isArray(post?.hashtags) && post.hashtags.length) {
      return post.hashtags;
    }
    return caption.match(/#[\w]+/g) || [];
  }, [post?.hashtags, caption]);

  const authorUsername =
    post?.author?.username ||
    post?.authorUsername ||
    author?.username ||
    "unknown";

  const authorFromUsers = Array.isArray(users)
    ? users.find(
        (u) =>
          String(u?.chatId) === String(post?.chatId || post?.author?.chatId)
      )
    : null;

  const authorAvatarUrl =
    post?.author?.profileImageUrl ||
    post?.author?.avatarUrl ||
    author?.profileImageUrl ||
    authorFromUsers?.profileImageUrl ||
    null;

  const soundLabel = post?.sound?.isOriginal
    ? `Original Sound - ${authorUsername}`
    : post?.sound?.title
    ? `${post.sound.title}${post.sound.artist ? ` - ${post.sound.artist}` : ""}`
    : `Original Sound - ${authorUsername}`;

  const isLiked = !!post?.likedByMe;

  const isSeries = post?.type === "series";
const seriesTitle = post?.seriesTitle || "Untitled series";
const viewsCount = post?.viewsCount ?? 0;

  /* ---------- navigation ---------- */

  const goTo = useCallback(
    (delta) =>
      setIndex((i) => Math.min(Math.max(i + delta, 0), posts.length - 1)),
    [posts.length, setIndex]
  );

  const handleWheel = (e) => {
    if (showComments) return;
    if (wheelLockRef.current || Math.abs(e.deltaY) < 30) return;
    wheelLockRef.current = true;
    goTo(e.deltaY > 0 ? 1 : -1);
    setTimeout(() => {
      wheelLockRef.current = false;
    }, 500);
  };

  const handleTouchStart = (e) => {
    touchStartYRef.current = showComments ? null : e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartYRef.current == null) return;
    const dy = touchStartYRef.current - e.changedTouches[0].clientY;
    touchStartYRef.current = null;
    if (Math.abs(dy) > 50) goTo(dy > 0 ? 1 : -1);
  };

  const handleKeyDown = (e) => {
    if (showComments) return;
    if (e.key === "ArrowDown") goTo(1);
    else if (e.key === "ArrowUp") goTo(-1);
    else if (e.key === "Escape") onClose();
  };

  useEffect(() => {
    rootRef.current?.focus();
  }, []);

  // Keep the index valid if the list shrinks (e.g. a failed optimistic post).
  useEffect(() => {
    if (!posts.length) onClose();
    else if (index >= posts.length) setIndex(posts.length - 1);
  }, [posts.length, index, onClose, setIndex]);

  /* ---------- playback ---------- */

  const tryPlay = () => {
    const v = videoRef.current;
    if (!v || userPausedRef.current) return;
    v.volume = 1;
    const p = v.play();
    if (p && typeof p.catch === "function") {
      p.catch((err) => {
        if (err && err.name === "AbortError") return;
        if (!v.muted) {
          v.muted = true;
          setIsMuted(true);
          v.play().catch(() => {});
        }
      });
    }
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = isMuted || hasCustomSound;
    tryPlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMuted, hasCustomSound, post?.postId]);

  const playSoundAudio = () => {
    const a = soundAudioRef.current;
    if (!a) return;
    const pr = a.play();
    if (pr && typeof pr.then === "function") {
      pr.then(() => setSoundBlocked(false)).catch((err) => {
        if (err && err.name === "AbortError") return;
        setSoundBlocked(true);
      });
    }
  };

  // Sound track for posts that use a sound: plays / pauses with the video.
  useEffect(() => {
    const a = soundAudioRef.current;
    if (!a) return;
    a.muted = isMuted;
    if (hasCustomSound && !isPaused) playSoundAudio();
    else a.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMuted, isPaused, hasCustomSound, post?.postId]);

  // Blocked sound starts on the user's next touch / key press.
  useEffect(() => {
    if (!soundBlocked || !hasCustomSound || isPaused) return;
    const retry = () => playSoundAudio();
    const events = ["pointerup", "touchend", "keydown"];
    events.forEach((ev) => window.addEventListener(ev, retry, { once: true }));
    return () => events.forEach((ev) => window.removeEventListener(ev, retry));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soundBlocked, hasCustomSound, isPaused, post?.postId]);

  // Never leave audio running after the viewer closes.
  useEffect(() => {
    return () => {
      videoRef.current?.pause();
      soundAudioRef.current?.pause();
    };
  }, []);

  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setIsScrubbing(false);
    setIsPaused(false);
    setSoundBlocked(false);
    setCaptionExpanded(false);
    userPausedRef.current = false;
  }, [post?.postId]);

  // Count a view each time a different post is shown (skip optimistic ones —
  // the server doesn't know them yet).
  useEffect(() => {
    if (!post?.postId || !type || post.isOptimistic) return;
    onTrackMediaView?.({ postId: post.postId, mediaType: type });
  }, [post?.postId, post?.isOptimistic, type, onTrackMediaView]);

  const handleVideoTap = () => {
    // Photo + sound: tap toggles the sound (there is no <video>)
    if (isPhotoSound) {
      const pa = soundAudioRef.current;
      if (!pa) return;
      if (isMuted) {
        pa.muted = false;
        setIsMuted(false);
        playSoundAudio();
        return;
      }
      if (isPaused) {
        userPausedRef.current = false;
        setIsPaused(false);
        playSoundAudio(); // inside the tap gesture
      } else {
        userPausedRef.current = true;
        setIsPaused(true);
        pa.pause();
      }
      return;
    }

    const v = videoRef.current;
    if (!v) return;
    const a = soundAudioRef.current;

    // Autoplay had to fall back to muted: the first tap unmutes.
    if (isMuted) {
      v.muted = hasCustomSound; // a sound post keeps its own video muted
      if (a && hasCustomSound) {
        a.muted = false;
        if (!v.paused) playSoundAudio();
      }
      setIsMuted(false);
      return;
    }

    if (v.paused) {
      userPausedRef.current = false;
      v.play().catch(() => {});
      if (a && hasCustomSound) playSoundAudio(); // inside the tap gesture
    } else {
      userPausedRef.current = true;
      v.pause();
      if (a) a.pause();
    }
  };

  const seekFromEvent = (e) => {
    const v = isPhotoSound ? soundAudioRef.current : videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    v.currentTime = ratio * duration;
    setCurrentTime(v.currentTime);
  };

  /* ---------- actions ---------- */

  const handleLike = () => post?.postId && onLikeMediaPost?.(post.postId);

  const handleSupport = () => {
    if (!post?.postId) return;
    onSupportMediaPost?.({ postId: post.postId, timestamp: Date.now() });
  };

  const handleShare = async () => {
    if (!post?.postId) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `@${authorUsername}`,
          text: captionBody,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
      }
    } catch {}
  };

  if (!post) return null;

  const hide = showComments ? styles.hiddenWhileComments : null;

  return (
    <div
      ref={rootRef}
      style={embedded ? { ...styles.viewer, ...styles.viewerEmbedded } : styles.viewer}
      tabIndex={0}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onKeyDown={handleKeyDown}
    >
      {/* Media stage — shrinks above the comment sheet (same as Moment) */}
      <div
        style={{
          ...styles.mediaStage,
          ...(showComments ? styles.mediaStageShrunk : null),
        }}
      >
        {url && type === "video" ? (
          <video
            key={post.postId}
            ref={videoRef}
            src={url}
            poster={thumb || undefined}
            style={styles.media}
            playsInline
            loop
            muted={isMuted || hasCustomSound}
            onClick={handleVideoTap}
            onLoadedData={tryPlay}
            onPlay={() => setIsPaused(false)}
            onPause={() => setIsPaused(true)}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
            onDurationChange={(e) => setDuration(e.currentTarget.duration || 0)}
            onTimeUpdate={(e) => {
              const t = e.currentTarget.currentTime;
              if (!isScrubbing) setCurrentTime(t);
              // video looped -> restart the sound too
              if (hasCustomSound && t + 0.5 < lastVideoTimeRef.current) {
                const a = soundAudioRef.current;
                if (a) a.currentTime = postSound?.startTime || 0;
              }
              lastVideoTimeRef.current = t;
            }}
          />
        ) : url ? (
          <img
            src={url}
            alt={captionBody || "Post"}
            style={styles.media}
            onClick={handleVideoTap}
          />
        ) : (
          <div style={styles.emptyMedia} />
        )}

        {(type === "video" || isPhotoSound) && isPaused ? (
          <div style={styles.pausedIcon}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="#fff">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        ) : null}
      </div>

      {/* Sound track for posts that use a sound (video itself is muted) */}
      {hasCustomSound ? (
        <audio
          key={post.postId}
          ref={soundAudioRef}
          src={postSound.url}
          loop
          preload="auto"
          onLoadedMetadata={(e) => {
            if (postSound.startTime) e.currentTarget.currentTime = postSound.startTime;
            if (isPhotoSound) {
              const d = e.currentTarget.duration;
              setDuration(Number.isFinite(d) ? d : 0);
            }
          }}
          onDurationChange={(e) => {
            if (!isPhotoSound) return;
            const d = e.currentTarget.duration;
            setDuration(Number.isFinite(d) ? d : 0);
          }}
          onTimeUpdate={(e) => {
            if (isPhotoSound && !isScrubbing) setCurrentTime(e.currentTarget.currentTime);
          }}
          style={{ display: "none" }}
        />
      ) : null}

      <div style={{ ...styles.bottomScrim, ...hide }} />
      <div style={{ ...styles.topScrim, ...hide }} />

      {/* Progress bar (video, or photo with a sound) */}
      {(type === "video" || isPhotoSound) && duration > 0 ? (
        <div
          style={{ ...styles.progressWrap, ...hide }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            setIsScrubbing(true);
            seekFromEvent(e);
          }}
          onPointerMove={(e) => isScrubbing && seekFromEvent(e)}
          onPointerUp={() => setIsScrubbing(false)}
          onPointerCancel={() => setIsScrubbing(false)}
        >
          <span
            style={{
              ...styles.timeLabel,
              fontSize: isScrubbing ? 18 : 11,
              opacity: isScrubbing ? 1 : 0.85,
            }}
          >
            {formatTime(currentTime)}
          </span>
          <span
            style={{
              ...styles.timeLabel,
              ...styles.timeLabelRight,
              fontSize: isScrubbing ? 18 : 11,
              opacity: isScrubbing ? 1 : 0.85,
            }}
          >
            {formatTime(duration)}
          </span>
          <div style={{ ...styles.progressTrack, height: isScrubbing ? 6 : 3 }}>
            <div
              style={{
                ...styles.progressFill,
                width: `${Math.min((currentTime / duration) * 100, 100)}%`,
              }}
            />
          </div>
        </div>
      ) : null}

      {/* Top bar — back + position */}
      <div style={{ ...styles.viewerTopBar, ...hide }}>
        <button
          type="button"
          onClick={onClose}
          style={styles.iconButton}
          aria-label="Back to posts"
        >
          <BackIcon />
        </button>
        <span style={styles.viewerCounter}>
          {index + 1} / {posts.length}
        </span>
        <span style={{ width: 40 }} />
      </div>
<div style={{ ...styles.bottomLeft, ...hide }}>
  <div style={styles.authorName}>@{authorUsername}</div>

  {captionBody || isSeries ? (
    <div style={styles.captionRow}>
      {captionBody ? (
        <p
          onClick={() => setCaptionExpanded((p) => !p)}
          style={{
            ...styles.caption,
            flex: 1,
            minWidth: 0,
            WebkitLineClamp: captionExpanded ? "unset" : 1,
          }}
        >
          {captionBody}
        </p>
      ) : null}

      {isSeries ? (
        <div style={styles.captionSeries}>
          <span style={styles.seriesTitle}>{seriesTitle}</span>
          <span style={styles.seriesMeta}>
            Season {post.seasonNumber ?? 1} · Episode {post.episodeNumber ?? 1}
          </span>
        </div>
      ) : null}
    </div>
  ) : null}

        {hashtags.length ? (
          <div style={styles.hashtagRow}>
            {hashtags.map((tag) => (
              <span key={tag} style={styles.hashtag}>
                {tag.startsWith("#") ? tag : `#${tag}`}
              </span>
            ))}
          </div>
        ) : null}

        <div style={styles.soundRow}>
          <MusicIcon size={14} color="#fff" />
          <span style={styles.soundLabel}>{soundLabel}</span>
        </div>
      </div>

      {/* Right action rail */}
      <div style={{ ...styles.actionRail, ...hide }}>
        <div style={styles.railAvatar}>
          {authorAvatarUrl ? (
            <img src={authorAvatarUrl} alt="" style={styles.fillImg} />
          ) : (
            <div style={styles.avatarFallback}>
              {authorUsername.slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>

        <ActionButton
          icon={<HeartIcon size={32} color={isLiked ? "#ff3b5c" : "#fff"} />}
          label={formatCount(post.likesCount)}
          onClick={handleLike}
        />
        <ActionButton
          icon={<CommentIcon size={32} color="#fff" />}
          label={formatCount(post.commentsCount)}
          onClick={() => setShowComments(true)}
        />
        <ActionButton
          icon={<EyeIcon size={32} color="#fff" />}
      label={formatCount(viewsCount)}
        />
        <ActionButton
          icon={<SupportIcon size={32} color="#fff" />}
          label={formatCount(post.supportsCount)}
          onClick={onSupportMediaPost ? handleSupport : undefined}
        />
        <ActionButton
          icon={<ShareIcon size={32} color="#fff" />}
          label={formatCount(post.repostsCount)}
          onClick={handleShare}
        />
      </div>

      {showComments ? (
        <MediaComment
          post={post}
          comments={mediaCommentsByPost?.[post.postId] || post.comments || []}
          myChatId={myChatId}
          users={users}
          currentUser={currentUser}
          avatarUrl={avatarUrl}
          onBack={() => setShowComments(false)}
          onAddComment={onAddMediaComment}
          onLikeComment={onLikeMediaComment}
          onSupportComment={onSupportMediaComment}
          onViewCommentEarnings={onViewMediaCommentEarnings}
          onTrackCommentView={onTrackMediaCommentView}
        />
      ) : null}
    </div>
  );
}

function ActionButton({ icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      style={styles.actionButton}
    >
      <span style={styles.actionIcon}>{icon}</span>
      <span style={styles.actionLabel}>{label}</span>
    </button>
  );
}

/* =========================================================
   🧑 MY MEDIA POSTS — profile grid
========================================================= */

export default function MyMediaPost({
  myMediaPosts = [],
  viewedUser = null, // set when looking at ANOTHER author's posts
  myChatId,
  myUserName,
  myDisplayName,
  currentUser,
  avatarUrl,
  users,
  mediaCommentsByPost = {},

  onLikeMediaPost,
  onAddMediaComment,
  onLikeMediaComment,
  onSupportMediaPost,
  onSupportMediaComment,
  onViewMediaCommentEarnings,
  onTrackMediaCommentView,
  onTrackMediaView,

  // Optional infinite scroll (same contract as MyPost.js)
  onLoadMore,
  hasMore = false,
  loading = false,

  onBack,
  embedded = false,
  className,
  style,
}) {
  const [viewerIndex, setViewerIndex] = useState(null); // null = grid
  const sentinelRef = useRef(null);

  // Newest first, like a TikTok profile.
  const posts = useMemo(
    () =>
      [...(myMediaPosts || [])]
        .filter((p) => p?.postId)
        .sort(
          (a, b) =>
            new Date(b.createdAt || 0).getTime() -
            new Date(a.createdAt || 0).getTime()
        ),
    [myMediaPosts]
  );

  const isViewingOther = !!viewedUser;

  const username = isViewingOther
    ? viewedUser.username || viewedUser.displayName || "user"
    : myUserName || "me";

  const displayName = isViewingOther
    ? viewedUser.displayName || viewedUser.username
    : myDisplayName;

  const profileUrl = isViewingOther
    ? viewedUser.profileImageUrl || viewedUser.avatarUrl || null
    : avatarUrl ||
      currentUser?.profileImageUrl ||
      currentUser?.avatarUrl ||
      null;

  const totals = useMemo(
    () =>
      posts.reduce(
        (acc, p) => {
          acc.likes += Number(p.likesCount) || 0;
          acc.views += Number(p.viewsCount) || 0;
          return acc;
        },
        { likes: 0, views: 0 }
      ),
    [posts]
  );

  // Infinite scroll — load more when the sentinel scrolls into view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !onLoadMore || !hasMore || loading) return;
    if (typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { rootMargin: "400px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [onLoadMore, hasMore, loading, posts.length]);

  // Close the viewer if we switch to a different author's posts.
  useLayoutEffect(() => {
    setViewerIndex(null);
  }, [viewedUser?.chatId]);

  const openPost = useCallback(
    (postId) => {
      const i = posts.findIndex((p) => p.postId === postId);
      if (i >= 0) setViewerIndex(i);
    },
    [posts]
  );

  const closeViewer = useCallback(() => setViewerIndex(null), []);

  // Embedded: lift the viewer out of the overlay's scroll area into <body>.
  const wrapViewer = (node) =>
    embedded && typeof document !== "undefined"
      ? createPortal(node, document.body)
      : node;

  return (
    <div
      className={className}
      style={{ ...styles.page, ...(embedded ? styles.pageEmbedded : null), ...style }}
    >
      {/* ---------- scrolling profile + grid ---------- */}
      <div style={embedded ? styles.scrollEmbedded : styles.scroll}>
        {!embedded ? (
        <>
        <div style={styles.header}>
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              style={styles.iconButton}
              aria-label="Back"
            >
              <BackIcon />
            </button>
          ) : (
            <span style={{ width: 40 }} />
          )}
          <div style={styles.headerTitle}>@{username}</div>
          <span style={{ width: 40 }} />
        </div>

        <div style={styles.profile}>
          <div style={styles.profileAvatar}>
            {profileUrl ? (
              <img src={profileUrl} alt="" style={styles.fillImg} />
            ) : (
              <div style={{ ...styles.avatarFallback, fontSize: 34 }}>
                {String(displayName || username).slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>

          {displayName ? (
            <div style={styles.displayName}>{displayName}</div>
          ) : null}

          <div style={styles.stats}>
            <Stat value={posts.length} label="Posts" />
            <div style={styles.statDivider} />
            <Stat value={totals.likes} label="Likes" />
            <div style={styles.statDivider} />
            <Stat value={totals.views} label="Views" />
          </div>
        </div>

        <div style={styles.gridTabBar}>
          <div style={styles.gridTab}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff">
              <rect x="3" y="3" width="8" height="8" rx="1.5" />
              <rect x="13" y="3" width="8" height="8" rx="1.5" />
              <rect x="3" y="13" width="8" height="8" rx="1.5" />
              <rect x="13" y="13" width="8" height="8" rx="1.5" />
            </svg>
            <span style={styles.gridTabUnderline} />
          </div>
        </div>
        </>
        ) : null}

        {posts.length ? (
          <div style={styles.grid}>
            {posts.map((post) => (
              <PostTile key={post.postId} post={post} onOpen={openPost} />
            ))}
          </div>
        ) : loading ? (
          <div style={{ ...styles.emptyState, ...(embedded ? styles.emptyStateEmbedded : null) }}>Loading…</div>
        ) : (
          <div style={{ ...styles.emptyState, ...(embedded ? styles.emptyStateEmbedded : null) }}>
            {isViewingOther
              ? "No posts yet."
              : "You haven't posted anything yet."}
          </div>
        )}

        <div ref={sentinelRef} style={{ height: 1 }} />
        {loading && posts.length ? (
          <div style={{ ...styles.loadingMore, ...(embedded ? styles.emptyStateEmbedded : null) }}>Loading more…</div>
        ) : null}
        {!embedded ? <div style={{ height: "calc(24px + var(--bottom-tabs-h, 0px))" }} /> : null}
      </div>

      {/* ---------- full-screen viewer ---------- */}
      {viewerIndex !== null
        ? wrapViewer(
        <PostViewer
          embedded={embedded}
          posts={posts}
          index={viewerIndex}
          setIndex={setViewerIndex}
          onClose={closeViewer}
          author={
            isViewingOther
              ? viewedUser
              : { username: myUserName, profileImageUrl: profileUrl }
          }
          users={users}
          currentUser={currentUser}
          avatarUrl={avatarUrl}
          myChatId={myChatId}
          mediaCommentsByPost={mediaCommentsByPost}
          onLikeMediaPost={onLikeMediaPost}
          onAddMediaComment={onAddMediaComment}
          onLikeMediaComment={onLikeMediaComment}
          onSupportMediaPost={onSupportMediaPost}
          onSupportMediaComment={onSupportMediaComment}
          onViewMediaCommentEarnings={onViewMediaCommentEarnings}
          onTrackMediaCommentView={onTrackMediaCommentView}
          onTrackMediaView={onTrackMediaView}
        />
          )
        : null}
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div style={styles.stat}>
      <div style={styles.statValue}>{formatCount(value)}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  );
}

/* =========================================================
   🎨 STYLES
========================================================= */

// Bottom-pinned items clear the fixed mobile tab bar (published as
// --bottom-tabs-h by MobileChatPanel; 0px on desktop).
const TAB_H = "var(--bottom-tabs-h, 0px)";

const styles = {
  page: {
    position: "relative",
    width: "100%",
    height: "100%",
    minHeight: "100dvh",
    overflow: "hidden",
    background: "#000",
    fontFamily: "sans-serif",
    color: "#fff",
  },

  pageEmbedded: {
    height: "auto",
    minHeight: 0,
    overflow: "visible",
    background: "transparent",
    color: "inherit",
  },

  scrollEmbedded: { position: "static" },

  emptyStateEmbedded: { color: "#737373" },

  // Portal'd above UserInfoOverlay (z-50 / z-[70] / mobile tab bar z-[60]).
  // The tab bar is covered, so no bottom inset is needed.
  viewerEmbedded: {
    position: "fixed",
    zIndex: 100,
    "--bottom-tabs-h": "0px",
  },

  scroll: {
    position: "absolute",
    inset: 0,
    overflowY: "auto",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
  },

  /* ----- header / profile ----- */
  header: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "calc(8px + env(safe-area-inset-top, 0px)) 8px 8px",
    background: "rgba(0,0,0,0.92)",
    backdropFilter: "blur(8px)",
  },

  headerTitle: {
    fontSize: 16,
    fontWeight: 700,
    maxWidth: "60%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  iconButton: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "none",
    background: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  },

  profile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "12px 16px 16px",
  },

  profileAvatar: {
    width: 96,
    height: 96,
    borderRadius: "50%",
    overflow: "hidden",
    background: "#16a34a",
    flexShrink: 0,
  },

  displayName: {
    marginTop: 10,
    fontSize: 15,
    color: "rgba(255,255,255,0.75)",
  },

  stats: {
    marginTop: 14,
    display: "flex",
    alignItems: "center",
    gap: 28,
  },

  stat: { textAlign: "center", minWidth: 56 },
  statValue: { fontSize: 18, fontWeight: 700 },
  statLabel: { fontSize: 12, color: "rgba(255,255,255,0.6)", marginTop: 2 },
  statDivider: { width: 1, height: 22, background: "rgba(255,255,255,0.18)" },

  gridTabBar: {
    display: "flex",
    justifyContent: "center",
    borderTop: "1px solid rgba(255,255,255,0.12)",
    borderBottom: "1px solid rgba(255,255,255,0.12)",
  },

  gridTab: {
    position: "relative",
    padding: "10px 28px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  gridTabUnderline: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: -1,
    height: 2,
    background: "#fff",
  },

  /* ----- grid ----- */
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 2,
    paddingTop: 2,
  },

  tile: {
    position: "relative",
    aspectRatio: "9 / 16",
    padding: 0,
    border: "none",
    background: "#161616",
    overflow: "hidden",
    cursor: "pointer",
  },

  tileMedia: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  tileEmpty: { position: "absolute", inset: 0, background: "#1c1c1c" },

  tileScrim: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "35%",
    background: "linear-gradient(0deg, rgba(0,0,0,0.55), rgba(0,0,0,0))",
    pointerEvents: "none",
  },

  tileBadge: {
    position: "absolute",
    top: 6,
    right: 6,
    display: "flex",
    filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
  },

  tileViews: {
    position: "absolute",
    left: 6,
    bottom: 6,
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    textShadow: "0 1px 3px rgba(0,0,0,0.6)",
  },

  tilePending: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0,0,0,0.55)",
    fontSize: 12,
    fontWeight: 700,
  },

  emptyState: {
    padding: "48px 24px",
    textAlign: "center",
    fontSize: 15,
    color: "rgba(255,255,255,0.6)",
  },

  loadingMore: {
    padding: 16,
    textAlign: "center",
    fontSize: 13,
    color: "rgba(255,255,255,0.6)",
  },

  /* ----- viewer ----- */
  viewer: {
    position: "absolute",
    inset: 0,
    zIndex: 10,
    background: "#000",
    overflow: "hidden",
    touchAction: "none",
    outline: "none",
  },

  mediaStage: {
    position: "absolute",
    inset: 0,
    transformOrigin: "top center",
    transition:
      "transform 280ms cubic-bezier(0.22, 1, 0.36, 1), border-radius 280ms ease",
    overflow: "hidden",
    willChange: "transform",
  },

  mediaStageShrunk: {
    transform: "translateY(12px) scale(0.3)",
    borderRadius: 28,
  },

  hiddenWhileComments: {
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 160ms ease",
  },

  media: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  emptyMedia: { position: "absolute", inset: 0, background: "#111" },

  pausedIcon: {
    position: "absolute",
    top: "50%",
    left: "50%",
    width: 64,
    height: 64,
    transform: "translate(-50%, -50%)",
    borderRadius: "50%",
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    zIndex: 3,
  },

  topScrim: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 120,
    background: "linear-gradient(180deg, rgba(0,0,0,0.4), rgba(0,0,0,0))",
    pointerEvents: "none",
  },

  bottomScrim: {
    position: "absolute",
    bottom: TAB_H,
    left: 0,
    right: 0,
    height: "45%",
    background: "linear-gradient(0deg, rgba(0,0,0,0.55), rgba(0,0,0,0))",
    pointerEvents: "none",
  },

  viewerTopBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "calc(8px + env(safe-area-inset-top, 0px)) 8px 0",
  },

  viewerCounter: {
    fontSize: 13,
    fontWeight: 600,
    textShadow: "0 1px 3px rgba(0,0,0,0.5)",
  },

  progressWrap: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: TAB_H,
    height: 24,
    display: "flex",
    alignItems: "flex-end",
    cursor: "pointer",
    touchAction: "none",
    zIndex: 5,
  },

  timeLabel: {
    position: "absolute",
    left: 16,
    bottom: 8,
    color: "#fff",
    fontWeight: 600,
    textShadow: "0 1px 3px rgba(0,0,0,0.6)",
    pointerEvents: "none",
    transition: "font-size 0.15s",
  },

  timeLabelRight: { left: "auto", right: 16 },

  progressTrack: {
    width: "100%",
    background: "rgba(255,255,255,0.3)",
    transition: "height 0.15s",
  },

  progressFill: { height: "100%", background: "#fff" },

  bottomLeft: {
    position: "absolute",
    left: 16,
    right: 84,
    bottom: `calc(34px + ${TAB_H})`,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    zIndex: 2,
  },

  authorName: {
    fontSize: 16,
    fontWeight: 700,
    textShadow: "0 1px 3px rgba(0,0,0,0.4)",
  },

  caption: {
    margin: 0,
    fontSize: 14.5,
    lineHeight: 1.35,
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    cursor: "pointer",
    textShadow: "0 1px 3px rgba(0,0,0,0.4)",
  },

  hashtagRow: { display: "flex", flexWrap: "wrap", gap: 8 },

  hashtag: {
    fontSize: 14.5,
    fontWeight: 600,
    textShadow: "0 1px 3px rgba(0,0,0,0.4)",
  },

  captionRow: { display: "flex", alignItems: "flex-start", gap: 10, marginTop: -4 },
captionSeries: {
  flex: "0 0 auto", maxWidth: "46%", marginLeft: "auto",
  display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, minWidth: 0,
},
seriesTitle: {
  color: "#fff", fontSize: 13, fontWeight: 700, maxWidth: "100%",
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  textShadow: "0 1px 3px rgba(0,0,0,0.5)",
},
seriesMeta: {
  color: "rgba(255,255,255,0.85)", fontSize: 11, fontWeight: 500,
  whiteSpace: "nowrap", textShadow: "0 1px 3px rgba(0,0,0,0.5)",
},

  soundRow: { display: "flex", alignItems: "center", gap: 6, marginTop: 2 },

  soundLabel: {
    fontSize: 13,
    fontWeight: 500,
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  actionRail: {
    position: "absolute",
    right: 10,
    bottom: `calc(34px + ${TAB_H})`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    zIndex: 2,
  },

  railAvatar: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    border: "2px solid #fff",
    background: "#333",
    overflow: "hidden",
    marginBottom: 4,
    flexShrink: 0,
  },

  actionButton: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
  },

  actionIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
  },

  actionLabel: {
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.1,
    marginTop: 1,
    textShadow: "0 1px 3px rgba(0,0,0,0.4)",
  },

  fillImg: { width: "100%", height: "100%", objectFit: "cover" },

  avatarFallback: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: 700,
  },
};